import { PLACE_KIND_ICONS, PLACE_KIND_LABELS } from '../format';

/**
 * A map pin, as HTML both engines can place: Yandex through a template layout,
 * Google through an overlay. A teardrop with the kind's icon, ringed in the
 * day's colour — so the map and the timeline read as the same object — and
 * filled with it when active.
 *
 * Colours come from the trip's data (a day's colour) or the theme's accent;
 * the hex fallback mirrors `--color-clay-500` because a pin is drawn outside
 * the React tree, where Tailwind classes do not reach.
 */

export const PIN_ACCENT = '#e2654a';

/**
 * The icon a pin wears. Where one lives gets a house — that pin is what a
 * traveller looks for on the map first; the rest follow the place kind.
 */
export const MAP_PIN_ICONS: Record<string, string> = {
  ...PLACE_KIND_ICONS,
  lodging: '🏠',
};

export interface PinPlace {
  id: string;
  name: string;
  kind: string;
  description?: string | null;
  optional?: boolean;
  dayIndex?: number | null;
  color?: string | null;
  unverified?: boolean;
}

export function pinSize(active: boolean): number {
  return active ? 44 : 34;
}

export function pinHtml(place: PinPlace, active: boolean): string {
  const emoji = MAP_PIN_ICONS[place.kind] ?? '📍';
  const accent = place.color ?? PIN_ACCENT;
  const size = pinSize(active);
  const lodging = place.kind === 'lodging';
  return `<div class="travessa-pin" style="
      width:${size}px;height:${size}px;
      display:grid;place-items:center;
      border-radius:50% 50% 50% 6px;
      transform:rotate(-45deg);
      background:${active || lodging ? accent : '#ffffff'};
      border:2px solid ${accent};
      box-shadow:0 6px 16px -4px rgba(11,15,20,.45);
      cursor:pointer;
      ${place.optional ? 'opacity:.72;' : ''}
    "><span style="transform:rotate(45deg);font-size:${active ? 18 : 15}px;line-height:1">${emoji}</span></div>`;
}

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/** The balloon / info window: name, kind and day, a line of description. */
export function popupHtml(place: PinPlace): string {
  const kind = PLACE_KIND_LABELS[place.kind] ?? place.kind;
  const day = place.dayIndex ? ` · день ${place.dayIndex}` : '';
  const description = place.description
    ? `<div style="margin-top:6px;max-width:16rem;font-size:12px;line-height:1.35">${escapeHtml(
        place.description.slice(0, 180),
      )}${place.description.length > 180 ? '…' : ''}</div>`
    : '';
  const unverified = place.unverified
    ? '<div style="margin-top:4px;font-size:12px;font-weight:500;color:#b26a00">🔎 требует проверки</div>'
    : '';
  return `<div style="font-family:inherit;color:#0b0f14"><strong style="display:block;font-size:14px">${escapeHtml(
    place.name,
  )}</strong><span style="display:block;margin-top:2px;font-size:12px;opacity:.7">${escapeHtml(kind)}${day}</span>${unverified}${description}</div>`;
}
